import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Download, ChevronDown, ChevronRight, Filter, X } from 'lucide-react';
import toast from 'react-hot-toast';
import { useAuthStore } from '../../stores/authStore';
import { useOrganizador } from '../../contexts/OrganizadorContext';
import { auditoriaService } from '../../services/auditoriaService';

// ── Constantes ───────────────────────────────────────────────────────────────

const ENTIDADES = [
  { value: '', label: 'Todas' },
  { value: 'usuario', label: 'Usuário (Login)' },
  { value: 'cliente', label: 'Cliente' },
  { value: 'leilao', label: 'Leilão' },
  { value: 'lote', label: 'Lote' },
  { value: 'comissao', label: 'Comissão' },
  { value: 'conta_pagar', label: 'Conta a Pagar' },
  { value: 'conta_receber', label: 'Conta a Receber' },
  { value: 'parcela', label: 'Parcela (Comprador/Vendedor)' },
  { value: 'pisteiro', label: 'Pisteiro' },
  { value: 'despesa', label: 'Despesa' },
  { value: 'recibo', label: 'Recibo' },
  { value: 'configuracao', label: 'Configuração' },
];

const ACOES = [
  { value: '', label: 'Todas' },
  { value: 'login', label: 'Login' },
  { value: 'criar', label: 'Criar' },
  { value: 'editar', label: 'Editar' },
  { value: 'excluir', label: 'Excluir' },
  { value: 'faturar', label: 'Faturar' },
  { value: 'desfaturar', label: 'Desfaturar' },
  { value: 'baixar', label: 'Baixar' },
  { value: 'estornar', label: 'Estornar' },
  { value: 'repasse', label: 'Repasse' },
  { value: 'estornar_repasse', label: 'Estornar Repasse' },
  { value: 'alterar_comissao', label: 'Alterar Comissão' },
  { value: 'aplicar_desconto', label: 'Aplicar Desconto' },
];

// ── Helpers ──────────────────────────────────────────────────────────────────

function formatDateTime(dateStr) {
  if (!dateStr) return '-';
  const d = new Date(dateStr);
  return d.toLocaleString('pt-BR', {
    day: '2-digit', month: '2-digit', year: 'numeric',
    hour: '2-digit', minute: '2-digit',
  });
}

function formatMoney(value) {
  if (value === null || value === undefined) return '-';
  return `R$ ${Number(value).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

function isMoneyField(key) {
  const moneyKeys = ['valor', 'valor_venda_total', 'valor_pago', 'valor_recebido',
    'desconto_valor', 'quem_trouxe_valor', 'valor_por_lote'];
  return moneyKeys.includes(key) || key.includes('valor');
}

function formatFieldValue(key, value) {
  if (value === null || value === undefined) return '-';
  if (isMoneyField(key)) return formatMoney(value);
  if (typeof value === 'boolean') return value ? 'Sim' : 'Não';
  return String(value);
}

function getAcaoBadgeColor(acao) {
  const colors = {
    login: 'bg-sky-100 text-sky-800',
    criar: 'bg-green-100 text-green-800',
    editar: 'bg-blue-100 text-blue-800',
    excluir: 'bg-red-100 text-red-800',
    faturar: 'bg-purple-100 text-purple-800',
    desfaturar: 'bg-orange-100 text-orange-800',
    baixar: 'bg-teal-100 text-teal-800',
    estornar: 'bg-rose-100 text-rose-800',
    repasse: 'bg-emerald-100 text-emerald-800',
    estornar_repasse: 'bg-pink-100 text-pink-800',
    alterar_comissao: 'bg-yellow-100 text-yellow-800',
    aplicar_desconto: 'bg-indigo-100 text-indigo-800',
  };
  return colors[acao] || 'bg-gray-100 text-gray-800';
}

function getAcaoLabel(acao) {
  const labels = {
    login: 'Login',
    criar: 'Criar',
    editar: 'Editar',
    excluir: 'Excluir',
    faturar: 'Faturar',
    desfaturar: 'Desfaturar',
    baixar: 'Baixar',
    estornar: 'Estornar',
    repasse: 'Repasse',
    estornar_repasse: 'Est. Repasse',
    alterar_comissao: 'Alt. Comissão',
    aplicar_desconto: 'Desc.',
  };
  return labels[acao] || acao;
}

// ── DiffView Component ───────────────────────────────────────────────────────

function DiffView({ dadosAnteriores, dadosNovos }) {
  if (!dadosAnteriores && !dadosNovos) return null;

  const allKeys = new Set([
    ...Object.keys(dadosAnteriores || {}),
    ...Object.keys(dadosNovos || {}),
  ]);

  // Only show dados_novos (creation)
  if (!dadosAnteriores && dadosNovos) {
    return (
      <div className="mt-3 p-4 bg-gray-50 rounded-lg border">
        <h4 className="text-sm font-semibold text-gray-700 mb-2">Novos Valores</h4>
        <div className="grid grid-cols-1 gap-1">
          {Object.entries(dadosNovos).map(([key, value]) => (
            <div key={key} className="flex items-center gap-2 text-sm py-1 px-2 bg-green-50 rounded">
              <span className="font-medium text-gray-600 min-w-[140px]">{key}:</span>
              <span className="text-green-700">{formatFieldValue(key, value)}</span>
            </div>
          ))}
        </div>
      </div>
    );
  }

  // Only show dados_anteriores (deletion)
  if (dadosAnteriores && !dadosNovos) {
    return (
      <div className="mt-3 p-4 bg-gray-50 rounded-lg border">
        <h4 className="text-sm font-semibold text-gray-700 mb-2">Valores Anteriores</h4>
        <div className="grid grid-cols-1 gap-1">
          {Object.entries(dadosAnteriores).map(([key, value]) => (
            <div key={key} className="flex items-center gap-2 text-sm py-1 px-2 bg-red-50 rounded">
              <span className="font-medium text-gray-600 min-w-[140px]">{key}:</span>
              <span className="text-red-700">{formatFieldValue(key, value)}</span>
            </div>
          ))}
        </div>
      </div>
    );
  }

  // Both: side by side diff
  return (
    <div className="mt-3 p-4 bg-gray-50 rounded-lg border">
      <div className="grid grid-cols-2 gap-4">
        <div>
          <h4 className="text-sm font-semibold text-gray-700 mb-2">Valores Anteriores</h4>
        </div>
        <div>
          <h4 className="text-sm font-semibold text-gray-700 mb-2">Novos Valores</h4>
        </div>
      </div>
      <div className="grid grid-cols-1 gap-1">
        {[...allKeys].map((key) => {
          const oldVal = dadosAnteriores?.[key];
          const newVal = dadosNovos?.[key];
          const changed = JSON.stringify(oldVal) !== JSON.stringify(newVal);
          return (
            <div key={key} className={`grid grid-cols-2 gap-4 text-sm py-1 px-2 rounded ${changed ? 'bg-yellow-50' : ''}`}>
              <div className="flex items-center gap-2">
                <span className="font-medium text-gray-600 min-w-[120px]">{key}:</span>
                <span className={changed ? 'text-red-600 line-through' : 'text-gray-700'}>
                  {formatFieldValue(key, oldVal)}
                </span>
              </div>
              <div className="flex items-center gap-2">
                <span className="font-medium text-gray-600 min-w-[120px]">{key}:</span>
                <span className={changed ? 'text-green-600 font-medium' : 'text-gray-700'}>
                  {formatFieldValue(key, newVal)}
                </span>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ── Main Component ───────────────────────────────────────────────────────────

const AuditoriaLog = () => {
  const navigate = useNavigate();
  const { user } = useAuthStore();
  const { organizadorAtivo } = useOrganizador();

  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [total, setTotal] = useState(0);
  const [expandedRow, setExpandedRow] = useState(null);

  // Filters
  const [filtros, setFiltros] = useState({
    usuario_id: '',
    entidade: '',
    acao: '',
    data_inicio: '',
    data_fim: '',
  });

  // Admin check
  const isAdmin = user?.permission_level === 'admin' || user?.permission_level === 'super_admin';

  useEffect(() => {
    if (!isAdmin) {
      navigate('/configuracoes');
      toast.error('Acesso restrito a administradores');
    }
  }, [isAdmin, navigate]);

  const carregarLogs = useCallback(async () => {
    if (!organizadorAtivo?.id) return;
    setLoading(true);
    try {
      const res = await auditoriaService.listar(organizadorAtivo.id, filtros, page, 50);
      const data = res.data;
      setLogs(data.items || []);
      setTotalPages(data.total_pages || 0);
      setTotal(data.total || 0);
    } catch (err) {
      if (err.response?.status === 403) {
        toast.error('Acesso restrito a administradores');
        navigate('/configuracoes');
      } else {
        toast.error('Erro ao carregar logs de auditoria');
      }
    } finally {
      setLoading(false);
    }
  }, [organizadorAtivo?.id, filtros, page, navigate]);

  useEffect(() => {
    carregarLogs();
  }, [carregarLogs]);

  const handleFiltroChange = (key, value) => {
    setFiltros(prev => ({ ...prev, [key]: value }));
    setPage(1);
  };

  const limparFiltros = () => {
    setFiltros({ usuario_id: '', entidade: '', acao: '', data_inicio: '', data_fim: '' });
    setPage(1);
  };

  const handleExportar = async (formato) => {
    if (!organizadorAtivo?.id) return;
    try {
      const res = await auditoriaService.exportar(organizadorAtivo.id, filtros, formato);
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `auditoria.${formato}`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success(`Exportação ${formato.toUpperCase()} realizada`);
    } catch {
      toast.error('Erro ao exportar');
    }
  };

  if (!isAdmin) return null;

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 px-6 py-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <button
              onClick={() => navigate('/configuracoes')}
              className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <ArrowLeft size={20} />
            </button>
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Log de Auditoria</h1>
              <p className="text-gray-600 text-sm mt-0.5">
                Histórico de operações financeiras ({total} registros)
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => handleExportar('csv')}
              className="flex items-center gap-2 px-3 py-2 text-sm bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
            >
              <Download size={16} />
              CSV
            </button>
            <button
              onClick={() => handleExportar('xlsx')}
              className="flex items-center gap-2 px-3 py-2 text-sm bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
            >
              <Download size={16} />
              XLSX
            </button>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white border-b border-gray-200 px-6 py-3">
        <div className="flex items-center gap-3 flex-wrap">
          <Filter size={16} className="text-gray-400" />
          <select
            value={filtros.entidade}
            onChange={(e) => handleFiltroChange('entidade', e.target.value)}
            className="px-3 py-1.5 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          >
            {ENTIDADES.map(e => (
              <option key={e.value} value={e.value}>{e.label}</option>
            ))}
          </select>
          <select
            value={filtros.acao}
            onChange={(e) => handleFiltroChange('acao', e.target.value)}
            className="px-3 py-1.5 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          >
            {ACOES.map(a => (
              <option key={a.value} value={a.value}>{a.label}</option>
            ))}
          </select>
          <input
            type="date"
            value={filtros.data_inicio}
            onChange={(e) => handleFiltroChange('data_inicio', e.target.value)}
            className="px-3 py-1.5 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            placeholder="Data início"
          />
          <input
            type="date"
            value={filtros.data_fim}
            onChange={(e) => handleFiltroChange('data_fim', e.target.value)}
            className="px-3 py-1.5 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            placeholder="Data fim"
          />
          {(filtros.entidade || filtros.acao || filtros.data_inicio || filtros.data_fim) && (
            <button
              onClick={limparFiltros}
              className="flex items-center gap-1 px-2 py-1.5 text-sm text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <X size={14} />
              Limpar
            </button>
          )}
        </div>
      </div>

      {/* Table */}
      <div className="px-6 py-4">
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
            </div>
          ) : logs.length === 0 ? (
            <div className="text-center py-12 text-gray-500">
              Nenhum registro de auditoria encontrado
            </div>
          ) : (
            <table className="w-full">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="w-8 px-4 py-3"></th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Data/Hora</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Usuário</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Ação</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Entidade</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Descrição</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">IP</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {logs.map((log) => (
                  <LogRow
                    key={log.id}
                    log={log}
                    expanded={expandedRow === log.id}
                    onToggle={() => setExpandedRow(expandedRow === log.id ? null : log.id)}
                  />
                ))}
              </tbody>
            </table>
          )}
        </div>

        {/* Pagination */}
        {total > 0 && (
          <div className="flex items-center justify-between mt-4">
            <p className="text-sm text-gray-600">
              Página {page} de {totalPages || 1} ({total} registros)
            </p>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setPage(p => Math.max(1, p - 1))}
                disabled={page <= 1}
                className="px-3 py-1.5 text-sm border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Anterior
              </button>
              <button
                onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                disabled={page >= totalPages}
                className="px-3 py-1.5 text-sm border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Próxima
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

// ── LogRow Component ─────────────────────────────────────────────────────────

function LogRow({ log, expanded, onToggle }) {
  const hasDiff = log.dados_anteriores || log.dados_novos;

  return (
    <>
      <tr
        className={`hover:bg-gray-50 transition-colors ${hasDiff ? 'cursor-pointer' : ''}`}
        onClick={hasDiff ? onToggle : undefined}
      >
        <td className="px-4 py-3">
          {hasDiff && (
            expanded ? <ChevronDown size={16} className="text-gray-400" /> : <ChevronRight size={16} className="text-gray-400" />
          )}
        </td>
        <td className="px-4 py-3 text-sm text-gray-700 whitespace-nowrap">
          {formatDateTime(log.created_at)}
        </td>
        <td className="px-4 py-3 text-sm text-gray-700">
          {log.usuario_nome}
        </td>
        <td className="px-4 py-3">
          <span className={`inline-flex px-2 py-0.5 text-xs font-medium rounded-full ${getAcaoBadgeColor(log.acao)}`}>
            {getAcaoLabel(log.acao)}
          </span>
        </td>
        <td className="px-4 py-3 text-sm text-gray-700 capitalize">
          {log.entidade?.replace(/_/g, ' ')}
        </td>
        <td className="px-4 py-3 text-sm text-gray-700 max-w-md truncate">
          {log.descricao}
        </td>
        <td className="px-4 py-3 text-xs text-gray-400 font-mono whitespace-nowrap">
          {log.ip_address || (log.dados_novos?.ip) || '—'}
        </td>
      </tr>
      {expanded && hasDiff && (
        <tr>
          <td colSpan={7} className="px-4 pb-4">
            <DiffView dadosAnteriores={log.dados_anteriores} dadosNovos={log.dados_novos} />
          </td>
        </tr>
      )}
    </>
  );
}

export default AuditoriaLog;
